/**
 * PlpImageSwiper — swipeable image area for PLP cards (mobile only).
 * Lazy-fetches all product images from /products/{handle}.json on first touch.
 * Animates image changes with the same discrete slide used by the PDP mobile
 * gallery (sections/main-product.liquid `navigateToImage`): the current image
 * slides out with translateX(±100%) + opacity 0, the src is swapped, and the
 * new image slides in from the opposite side (transition 0.2s ease).
 * On desktop it renders a plain image link (no swipe).
 */
(function () {
  const { html, useState, useEffect, useRef } = window.preact;

  /* Must match the list-card mobile breakpoint in plp-card-list.css and
     algolia-native-overrides.css — the compact card runs up to 1360px. */
  var MOBILE_MQ = '(max-width: 1360px)';
  function isMobile() {
    return window.matchMedia && window.matchMedia(MOBILE_MQ).matches;
  }

  // Gallery fetch — batched Storefront GraphQL, NOT /products/{handle}.json.
  // The REST endpoint rate-limits hard (429) when a grid of cards fetches
  // eagerly on mount, and a 429'd card never shows its swipe bar. Pending
  // handles are collected for a tick, then resolved in chunks of 10 through
  // one aliased products query each, paced + retried on THROTTLED — same
  // playbook as algolia-discount-checker.js.
  const GALLERY_CHUNK = 10;
  const _cache = new Map(); // handle → Promise<string[]>
  let _queue = [];
  let _flushTimer = null;

  function fetchProductImages(handle) {
    if (!handle) return Promise.resolve([]);
    if (_cache.has(handle)) return _cache.get(handle);
    const p = new Promise(function (resolve) {
      _queue.push({ handle: handle, resolve: resolve });
      if (!_flushTimer) _flushTimer = setTimeout(_flushQueue, 50);
    });
    _cache.set(handle, p);
    return p;
  }

  function _flushQueue() {
    _flushTimer = null;
    const pending = _queue.splice(0);
    for (let i = 0; i < pending.length; i += GALLERY_CHUNK) {
      // No manual stagger needed — the StorefrontClient queue paces dispatches.
      _fetchGalleryChunk(pending.slice(i, i + GALLERY_CHUNK));
    }
  }

  function _fetchGalleryChunk(items) {
    const query = 'query {' + items.map(function (it, i) {
      return ' p' + i + ': product(handle: ' + JSON.stringify(it.handle) + ')' +
        ' { images(first: 12) { nodes { url(transform: {maxWidth: 600, maxHeight: 600}) } } }';
    }).join('') + ' }';

    // Shared StorefrontClient queue — pacing, THROTTLED retries and backoff
    // are centralized there, so this no longer manages its own retry loop.
    if (!window.StorefrontClient) {
      items.forEach(function (it) { _cache.delete(it.handle); it.resolve([]); });
      return;
    }
    window.StorefrontClient.request(query, {})
      .then(function (json) {
        items.forEach(function (it, i) {
          const node = json && json.data && json.data['p' + i];
          const imgs = ((node && node.images && node.images.nodes) || [])
            .map(function (n) { return n.url; }).filter(Boolean);
          // Don't cache misses — the next mount (e.g. a filter re-render) retries
          // instead of permanently hiding the swipe bar.
          if (imgs.length === 0) _cache.delete(it.handle);
          it.resolve(imgs);
        });
      })
      .catch(function () {
        items.forEach(function (it) {
          _cache.delete(it.handle);
          it.resolve([]);
        });
      });
  }

  /**
   * Props:
   *   url         {string}  — product URL (href of the wrap anchor)
   *   handle      {string}  — product handle, used to fetch images
   *   mainImage   {string}  — initial image shown before fetch
   *   alt         {string}  — img alt text
   *   className   {string}  — class applied to the <a> wrap
   *   imgWidth    {number}
   *   imgHeight   {number}
   *   badge       {vnode}   — optional badge element rendered inside the wrap
   */
  function PlpImageSwiper({ url, handle, mainImage, alt, className, imgWidth, imgHeight, badge }) {
    const [images, setImages] = useState(mainImage ? [mainImage] : []);
    const [index, setIndex] = useState(0);
    // Reactive breakpoint: re-render on viewport changes so resizing a desktop
    // window into the mobile range swaps the desktop scrubber for the mobile
    // swipe bar (a mount-time check left the wrong branch in the DOM).
    const [mobile, setMobile] = useState(isMobile());
    useEffect(function () {
      if (!window.matchMedia) return;
      var mq = window.matchMedia(MOBILE_MQ);
      function onChange(e) { setMobile(e.matches); }
      if (mq.addEventListener) mq.addEventListener('change', onChange);
      else mq.addListener(onChange);
      return function () {
        if (mq.removeEventListener) mq.removeEventListener('change', onChange);
        else mq.removeListener(onChange);
      };
    }, []);
    const imgRef = useRef(null);
    const idxRef = useRef(0);
    const imagesRef = useRef(images);
    const swipeStartX = useRef(0);
    const swipeStartY = useRef(0);
    const horizontal = useRef(false);
    const swiped = useRef(false);
    const animating = useRef(false);
    const fetched = useRef(false);
    const scrollRef = useRef(null);
    const barDragging = useRef(false);

    imagesRef.current = images;
    idxRef.current = index;

    // When the parent changes mainImage (variant selection), snap back to index 0
    // so the variant image is always visible regardless of swiper position.
    useEffect(function () {
      setIndex(0);
      if (scrollRef.current) scrollRef.current.scrollLeft = 0;
      if (imgRef.current) {
        imgRef.current.style.transition = 'none';
        imgRef.current.style.transform = 'translateX(0)';
        imgRef.current.style.opacity = '1';
      }
    }, [mainImage]);

    // The card's initial photo is the VARIANT image (Algolia hit.image), but the
    // fetched gallery comes in product-gallery order — scrubbing back to the
    // start would swap in gallery[0] instead of the photo the card opened with.
    // Reorder so the variant image leads and position 0 always matches.
    //
    // The gallery is fetched with a 600x600 transform, which Shopify encodes IN
    // THE PATH ("..._600x600.png") while Algolia's variant image has no suffix —
    // a plain path compare never matches, so the variant image was prepended ON
    // TOP of its gallery copy and every card showed a duplicate slide. Strip the
    // size suffix before comparing.
    function imageKey(u) {
      return u.split('?')[0].replace(/_(\d+x\d*|\d*x\d+)(?=\.[^./]+$)/, '');
    }

    function orderForMainImage(imgs) {
      if (!mainImage) return imgs;
      var mainPath = imageKey(mainImage);
      var idx = -1;
      for (var i = 0; i < imgs.length; i++) {
        if (imageKey(imgs[i]) === mainPath) { idx = i; break; }
      }
      if (idx === 0) return imgs;
      if (idx > 0) return [imgs[idx]].concat(imgs.slice(0, idx), imgs.slice(idx + 1));
      // Variant image truly not in the gallery list (e.g. CDN rename) — keep it first.
      return [mainImage].concat(imgs);
    }

    function loadImages() {
      if (fetched.current || !handle) return;
      fetched.current = true;
      fetchProductImages(handle).then(function (imgs) {
        if (imgs.length > 1) setImages(orderForMainImage(imgs));
      });
    }

    // Always-visible image scrubber: fetch the gallery as soon as the card mounts
    // (only the list view uses this swiper) so the scroll bar shows on multi-image
    // products without waiting for a hover/touch first.
    useEffect(function () { loadImages(); }, []);

    // Discrete slide animation matching the PDP mobile gallery.
    function navigateTo(targetIdx, direction) {
      var imgs = imagesRef.current;
      targetIdx = Math.max(0, Math.min(imgs.length - 1, targetIdx));
      if (targetIdx === idxRef.current || animating.current) return;
      var el = imgRef.current;
      if (!el) { setIndex(targetIdx); return; }

      animating.current = true;
      var slideOut = direction === 'left' ? 'translateX(-100%)' : 'translateX(100%)';
      var slideIn = direction === 'left' ? 'translateX(100%)' : 'translateX(-100%)';
      el.style.transition = 'transform 0.2s ease, opacity 0.2s ease';
      el.style.transform = slideOut;
      el.style.opacity = '0';
      setTimeout(function () {
        idxRef.current = targetIdx;
        el.src = imgs[targetIdx];
        el.style.transition = 'none';
        el.style.transform = slideIn;
        el.style.opacity = '0';
        requestAnimationFrame(function () {
          el.style.transition = 'transform 0.2s ease, opacity 0.2s ease';
          el.style.transform = 'translateX(0)';
          el.style.opacity = '1';
          setIndex(targetIdx); // advances the progress bar
          animating.current = false;
        });
      }, 200);
    }

    function onTouchStart(e) {
      loadImages();
      swipeStartX.current = e.touches[0].clientX;
      swipeStartY.current = e.touches[0].clientY;
      horizontal.current = false;
      swiped.current = false;
    }

    function onTouchMove(e) {
      var dx = e.touches[0].clientX - swipeStartX.current;
      var dy = e.touches[0].clientY - swipeStartY.current;
      if (!horizontal.current && Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
        horizontal.current = true;
      }
      if (horizontal.current) e.preventDefault();
    }

    function onTouchEnd(e) {
      var dx = e.changedTouches[0].clientX - swipeStartX.current;
      var dy = e.changedTouches[0].clientY - swipeStartY.current;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40 && imagesRef.current.length > 1) {
        swiped.current = true;
        navigateTo(idxRef.current + (dx < 0 ? 1 : -1), dx < 0 ? 'left' : 'right');
      }
    }

    function onLinkClick(e) {
      if (swiped.current) {
        e.preventDefault();
        swiped.current = false;
      }
    }

    // Mobile: drag the progress bar to scrub through the gallery, independent of
    // swiping the photo. Pointer events cover both touch and mouse. Maps the
    // pointer x-position along the bar to an image index and swaps instantly.
    function barScrub(bar, clientX) {
      var imgs = imagesRef.current;
      if (imgs.length < 2) return;
      var rect = bar.getBoundingClientRect();
      var frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      var idx = Math.round(frac * (imgs.length - 1));
      if (idx === idxRef.current) return;
      idxRef.current = idx;
      if (imgRef.current) {
        imgRef.current.style.transition = 'none';
        imgRef.current.style.transform = 'translateX(0)';
        imgRef.current.style.opacity = '1';
        imgRef.current.src = imgs[idx];
      }
      setIndex(idx);
    }
    function onBarDown(e) {
      e.stopPropagation();
      barDragging.current = true;
      if (e.pointerId != null && e.currentTarget.setPointerCapture) {
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
      }
      loadImages();
      barScrub(e.currentTarget, e.clientX);
    }
    function onBarMove(e) {
      if (!barDragging.current) return;
      e.stopPropagation();
      barScrub(e.currentTarget, e.clientX);
    }
    function onBarUp(e) { e.stopPropagation(); barDragging.current = false; }
    function onBarTap(e) { e.stopPropagation(); e.preventDefault(); } // don't follow the link on a bar tap

    // Desktop: scrubbing the horizontal scrollbar maps its position to an image
    // index and swaps the picture instantly (scrubber feel, no slide).
    function onDesktopScroll(e) {
      var el = e.currentTarget;
      var max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return;
      var imgs = imagesRef.current;
      var idx = Math.round((el.scrollLeft / max) * (imgs.length - 1));
      idx = Math.max(0, Math.min(imgs.length - 1, idx));
      if (idx === idxRef.current) return;
      idxRef.current = idx;
      if (imgRef.current) imgRef.current.src = imgs[idx];
      setIndex(idx);
    }

    // Desktop scrubber cursor: a circular cursor with a chevron pointing the way
    // a click would navigate (right = next photo, left = previous).
    function arrowCursor(dir, active) {
      if (!active) return 'default';
      var d = dir === 'left' ? 'M16 6l-8 9 8 9' : 'M8 6l8 9-8 9';
      // Plain chevron (no enclosing circle). A soft white halo underneath keeps
      // it legible over both light and dark photos.
      var svg = "<svg xmlns='http://www.w3.org/2000/svg' width='24' height='30'>"
        + "<path d='" + d + "' fill='none' stroke='white' stroke-width='5' stroke-linecap='round' stroke-linejoin='round' opacity='0.85'/>"
        + "<path d='" + d + "' fill='none' stroke='#252525' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'/>"
        + "</svg>";
      return 'url("data:image/svg+xml,' + encodeURIComponent(svg) + '") 12 15, pointer';
    }

    // Direction a click at the pointer would go, relative to the current image
    // position along the scrubber. Returns 'left' | 'right' | null.
    function scrubDir(e) {
      var imgs = imagesRef.current;
      if (imgs.length < 2) return null;
      var rect = e.currentTarget.getBoundingClientRect();
      var x = e.clientX - rect.left;
      var currentX = (idxRef.current / (imgs.length - 1)) * rect.width;
      return x >= currentX ? 'right' : 'left';
    }

    function onScrubMove(e) {
      var dir = scrubDir(e);
      if (!dir) return;
      var idx = idxRef.current;
      var canGo = dir === 'right' ? idx < imagesRef.current.length - 1 : idx > 0;
      e.currentTarget.style.cursor = arrowCursor(dir, canGo);
    }

    function onScrubClick(e) {
      var dir = scrubDir(e);
      if (!dir) return;
      var el = e.currentTarget;
      var imgs = imagesRef.current;
      var target = Math.max(0, Math.min(imgs.length - 1, idxRef.current + (dir === 'right' ? 1 : -1)));
      if (target === idxRef.current) return;
      var max = el.scrollWidth - el.clientWidth;
      el.scrollTo({ left: (target / (imgs.length - 1)) * max, behavior: 'smooth' });
    }

    // Desktop: image link with a horizontal scrollbar at the foot of the photo.
    // Images are lazy-fetched on hover, then the scrollbar appears.
    if (!mobile) {
      return html`
        <div class="${className || ''}">
          <a href="${url}" class="plp-swiper__link" onMouseEnter=${loadImages}>
            <img ref=${imgRef} src="${mainImage}" alt="${alt}" loading="lazy" width="${imgWidth || 247}" height="${imgHeight || 247}" />
            ${badge || null}
          </a>
          ${images.length > 1 && html`
            <div class="plp-swiper-scroll" ref=${scrollRef} onScroll=${onDesktopScroll} onMouseMove=${onScrubMove} onClick=${onScrubClick} aria-hidden="true">
              <div class="plp-swiper-scroll__track" style=${{ width: (images.length * 100) + '%' }}>
                ${images.map(function (_, i) { return html`<span class="plp-swiper-scroll__seg" key=${i} />`; })}
              </div>
            </div>
          `}
        </div>
      `;
    }

    // Mobile: same wrapper structure as desktop — the photo link (grey rounded
    // container, overflow:hidden) and the swipe bar are SIBLINGS. The bar must
    // live OUTSIDE the rounded/clipped photo box: inside it, the container's
    // border-radius clipping cut the bar's pill ends flat.
    return html`
      <div class="${className || ''}">
        <a
          href="${url}"
          class="plp-swiper__link"
          onClick=${onLinkClick}
          onTouchStart=${onTouchStart}
          onTouchMove=${onTouchMove}
          onTouchEnd=${onTouchEnd}
        >
          <img
            ref=${imgRef}
            src="${mainImage}"
            alt="${alt}"
            loading="lazy"
            width="${imgWidth || 247}"
            height="${imgHeight || 247}"
          />
          ${badge || null}
        </a>
        ${images.length > 1 && html`
          <div
            class="plp-swiper-bar"
            style=${{ touchAction: 'none' }}
            onPointerDown=${onBarDown}
            onPointerMove=${onBarMove}
            onPointerUp=${onBarUp}
            onPointerCancel=${onBarUp}
            onClick=${onBarTap}
          >
            <div
              class="plp-swiper-thumb"
              style=${{
                /* Figma sliding bar: fixed 64px thumb on the 155px track (rx 2.5),
                   position interpolated over the remaining run — not a 1/n-wide
                   scrollbar thumb. */
                width: '64px',
                left: 'calc((100% - 64px) * ' + (index / Math.max(images.length - 1, 1)) + ')'
              }}
            />
          </div>
        `}
      </div>
    `;
  }

  window.PlpImageSwiper = PlpImageSwiper;
})();
